import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/appIcon';
import Button from '../../../components/ui/Button';

const CARD =
    'bg-gray-900/80 border border-gray-800/90 rounded-2xl shadow-inner shadow-black/40';
const PANEL =
    'bg-gradient-to-b from-gray-950 via-gray-950/95 to-gray-900 border border-gray-800/90 rounded-2xl shadow-[0_25px_80px_rgba(0,0,0,0.65)]';

const FORMAT_STYLE = {
    pdf: {
        ring: 'border-red-400/50 bg-red-500/5 shadow-[0_0_22px_rgba(239,68,68,0.18)]',
        iconWrap: 'bg-red-500/15 border border-red-400/40',
        icon: 'text-red-300'
    },
    excel: {
        ring: 'border-emerald-400/50 bg-emerald-500/5 shadow-[0_0_22px_rgba(16,185,129,0.18)]',
        iconWrap: 'bg-emerald-500/15 border border-emerald-400/40',
        icon: 'text-emerald-300'
    },
    csv: {
        ring: 'border-cyan-400/50 bg-cyan-500/5 shadow-[0_0_22px_rgba(34,211,238,0.18)]',
        iconWrap: 'bg-cyan-500/15 border border-cyan-400/40',
        icon: 'text-cyan-300'
    }
};

const Input = (props) => (
    <input
        {...props}
        className={[
            'w-full bg-gray-950/50 border border-gray-800 rounded-xl px-3 py-2 text-sm text-gray-200',
            'placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50',
            props.className || ''
        ].join(' ')}
    />
);

const Select = (props) => (
    <select
        {...props}
        className={[
            'w-full bg-gray-950/50 border border-gray-800 rounded-xl px-3 py-2 text-sm text-gray-200',
            'focus:outline-none focus:ring-2 focus:ring-cyan-500/50',
            props.className || ''
        ].join(' ')}
    />
);

const ExportPanel = ({
    dateRange,
    onDateRangeChange, // opcional
    onExport,          // (payload) => Promise<void> | void
    exporting = false, // opcional: si lo controlas desde arriba
    error = null       // opcional: mostrar error real
}) => {
    const [exportFormat, setExportFormat] = useState('pdf');
    const [includeCharts, setIncludeCharts] = useState(true);
    const [exportRange, setExportRange] = useState('current');
    const [reportType, setReportType] = useState('all');
    const [password, setPassword] = useState('');
    const [customRange, setCustomRange] = useState({
        start: dateRange?.start || '',
        end: dateRange?.end || ''
    });

    const exportOptions = useMemo(
        () => [
            {
                format: 'pdf',
                label: 'PDF',
                description: 'Ideal para impresión y compartir',
                icon: 'FileText'
            },
            {
                format: 'excel',
                label: 'Excel',
                description: 'Datos estructurados para análisis',
                icon: 'Table'
            },
            {
                format: 'csv',
                label: 'CSV',
                description: 'Datos simples para importar',
                icon: 'FileSpreadsheet'
            }
        ],
        []
    );

    const reportTypes = useMemo(
        () => [
            { id: 'income-statement', name: 'Estado de Resultados', description: 'Ingresos, gastos y utilidades' },
            { id: 'balance-sheet', name: 'Balance General', description: 'Activos, pasivos y patrimonio' },
            { id: 'cash-flow', name: 'Flujo de Efectivo', description: 'Movimientos de efectivo' },
            { id: 'comparative', name: 'Reportes Comparativos', description: 'Tendencias y variaciones' },
            { id: 'clients', name: 'Clientes', description: 'Rentabilidad y actividad por cliente' },
            { id: 'all', name: 'Reporte Completo', description: 'Todos los reportes financieros' }
        ],
        []
    );

    const resolvedRange = useMemo(() => {
        if (exportRange === 'custom') return { start: customRange.start, end: customRange.end };
        // si no es custom, mandamos el key para que el backend resuelva
        return null;
    }, [exportRange, customRange]);

    const selectedLabel = exportOptions.find((o) => o.format === exportFormat)?.label ?? exportFormat;

    const handleExport = async () => {
        const payload = {
            format: exportFormat,         // 'pdf' | 'excel' | 'csv'
            reportType,                   // 'income-statement' | ... | 'all'
            includeCharts,                // boolean
            rangePreset: exportRange,     // 'current' | 'last-month' | ...
            range: resolvedRange,         // {start,end} si custom
            password: password?.trim() || null
        };

        // ✅ Nada de setTimeout dummy. El export real vive en onExport.
        await onExport?.(payload);
    };

    const style = FORMAT_STYLE[exportFormat] ?? FORMAT_STYLE.csv;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className={PANEL + ' p-4 sm:p-5'}>
                <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg sm:text-xl font-bold text-white truncate">Exportar Reportes</h3>
                            <span className="inline-flex text-[11px] font-mono px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/40">
                                {dateRange?.start || '—'} → {dateRange?.end || '—'}
                            </span>
                        </div>
                        <p className="text-cyan-200/80 text-xs sm:text-sm truncate">
                            Genera reportes en PDF / Excel / CSV
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Config */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Formato */}
                    <div className={CARD + ' p-4 sm:p-6'}>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-cyan-500/15 border border-cyan-400/40">
                                <Icon name="Download" size={18} className="text-cyan-300" />
                            </div>
                            <div>
                                <h4 className="text-sm sm:text-base font-semibold text-white">Formato de exportación</h4>
                                <p className="text-[11px] text-gray-400">Selecciona el tipo de archivo</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {exportOptions.map((opt) => {
                                const active = exportFormat === opt.format;
                                const s = FORMAT_STYLE[opt.format] ?? FORMAT_STYLE.csv;

                                return (
                                    <motion.button
                                        key={opt.format}
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => setExportFormat(opt.format)}
                                        className={[
                                            'p-4 rounded-2xl border text-left transition-all',
                                            active ? s.ring : 'border-gray-800 hover:border-gray-700 bg-gray-950/40'
                                        ].join(' ')}
                                        type="button"
                                    >
                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-3 ${s.iconWrap}`}>
                                            <Icon name={opt.icon} size={22} className={s.icon} />
                                        </div>
                                        <p className="font-semibold text-gray-100">{opt.label}</p>
                                        <p className="text-xs text-gray-400 mt-1">{opt.description}</p>
                                    </motion.button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Tipos de reporte */}
                    <div className={CARD + ' p-4 sm:p-6'}>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-cyan-500/15 border border-cyan-400/40">
                                <Icon name="FileText" size={18} className="text-cyan-300" />
                            </div>
                            <div>
                                <h4 className="text-sm sm:text-base font-semibold text-white">Tipos de reporte</h4>
                                <p className="text-[11px] text-gray-400">Elige qué vas a exportar</p>
                            </div>
                        </div>

                        <div className="space-y-2">
                            {reportTypes.map((r) => {
                                const active = reportType === r.id;
                                return (
                                    <button
                                        key={r.id}
                                        type="button"
                                        onClick={() => setReportType(r.id)}
                                        className={[
                                            'w-full flex items-start gap-3 p-3 rounded-2xl border text-left transition-all',
                                            active
                                                ? 'border-cyan-500/40 bg-cyan-500/10 shadow-[0_0_18px_rgba(34,211,238,0.18)]'
                                                : 'border-gray-800 bg-gray-950/40 hover:border-gray-700'
                                        ].join(' ')}
                                    >
                                        <span
                                            className={[
                                                'mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center',
                                                active ? 'border-cyan-400' : 'border-gray-600'
                                            ].join(' ')}
                                        >
                                            {active ? <span className="w-2.5 h-2.5 rounded-full bg-cyan-400" /> : null}
                                        </span>

                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-100">{r.name}</p>
                                            <p className="text-[11px] text-gray-400">{r.description}</p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Panel control */}
                <div className="space-y-6">
                    <div className={CARD + ' p-4 sm:p-6'}>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-cyan-500/15 border border-cyan-400/40">
                                <Icon name="Settings" size={18} className="text-cyan-300" />
                            </div>
                            <div>
                                <h4 className="text-sm sm:text-base font-semibold text-white">Opciones</h4>
                                <p className="text-[11px] text-gray-400">Ajustes adicionales</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {/* charts */}
                            <button
                                type="button"
                                onClick={() => setIncludeCharts((v) => !v)}
                                className="w-full flex items-center justify-between p-3 rounded-2xl bg-gray-950/40 border border-gray-800 hover:border-gray-700 transition"
                            >
                                <div className="text-left">
                                    <p className="text-sm font-semibold text-gray-100">Incluir gráficos</p>
                                    <p className="text-[11px] text-gray-400">Agrega visualizaciones al reporte</p>
                                </div>
                                <span className="inline-flex h-5 w-9 items-center rounded-full bg-black/60 border border-gray-600 p-0.5">
                                    <span
                                        className={[
                                            'h-4 w-4 rounded-full transition-transform',
                                            includeCharts ? 'translate-x-4 bg-emerald-400' : 'bg-gray-400'
                                        ].join(' ')}
                                    />
                                </span>
                            </button>

                            {/* range */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-300 mb-2">
                                    Rango de exportación
                                </label>
                                <Select value={exportRange} onChange={(e) => setExportRange(e.target.value)}>
                                    <option value="current">Período actual</option>
                                    <option value="last-month">Mes anterior</option>
                                    <option value="last-quarter">Trimestre anterior</option>
                                    <option value="last-year">Año anterior</option>
                                    <option value="custom">Personalizado</option>
                                </Select>

                                {exportRange === 'custom' && (
                                    <div className="grid grid-cols-2 gap-2 mt-2">
                                        <Input
                                            type="date"
                                            value={customRange.start}
                                            onChange={(e) => setCustomRange((p) => ({ ...p, start: e.target.value }))}
                                        />
                                        <Input
                                            type="date"
                                            value={customRange.end}
                                            onChange={(e) => setCustomRange((p) => ({ ...p, end: e.target.value }))}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* password */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-300 mb-2">
                                    Contraseña (opcional)
                                </label>
                                <Input
                                    type="password"
                                    placeholder="Proteger archivo"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                                <p className="text-[11px] text-gray-500 mt-1">
                                    * Solo aplica si tu backend soporta cifrado del archivo.
                                </p>
                            </div>

                            {error ? (
                                <div className="p-3 rounded-2xl bg-red-500/10 border border-red-500/30">
                                    <p className="text-xs text-red-200">{String(error)}</p>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {/* Acción */}
                    <motion.div
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        className="bg-gradient-to-br from-cyan-500/10 via-blue-500/10 to-indigo-500/10 border border-cyan-500/20 rounded-2xl p-4 sm:p-6 shadow-[0_25px_80px_rgba(8,47,73,0.55)]"
                    >
                        <div className="text-center">
                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 ${style.iconWrap}`}>
                                <Icon name="Download" size={26} className={style.icon} />
                            </div>

                            <p className="font-semibold text-gray-100">Generar reporte</p>
                            <p className="text-sm text-gray-400 mt-1">{selectedLabel} • {reportType}</p>

                            <div className="mt-4">
                                <Button
                                    type="button"
                                    onClick={handleExport}
                                    disabled={exporting}
                                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-xl shadow-cyan-500/30 py-3 min-h-[44px] disabled:opacity-50"
                                >
                                    <Icon name={exporting ? 'Loader' : 'Download'} className="w-4 h-4 mr-2" />
                                    {exporting ? 'Generando...' : 'Exportar'}
                                </Button>

                                {exporting ? (
                                    <p className="text-[11px] text-gray-400 mt-2">
                                        Preparando archivo para descarga...
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default ExportPanel;
